import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import { ROUTES } from '../../constants/routes';
import { useGetContactInfoQuery } from '../../store/siteContentApi';
import Logo from '../common/Logo';

// The public site's footer, rebuilt to the v5 reference.
//
// The reference's four link columns replace the old Services / Popular Tests
// / Company set, and with them go the admin-curated popular-tests list, the
// membership flag, the certification badge row and the oversized outlined
// wordmark — the reference has none of them. The address and email stay
// wired to the admin's ContactInfo so they can be corrected without a
// deploy; everything else here is static navigation.

// Mirrors the reference's own copy, used until ContactInfo supplies these.
const FALLBACK = {
  address: 'Street #5, Prakash Nagar, Begumpet, Hyderabad, Telangana',
  email: 'admin@mytrustlab.com',
};

// Column 2. "Build your own package", "Talk to a lab doctor" and "Tests in
// your city" point at pages still to be built; their routes already exist so
// these links do not have to be revisited when the pages land.
const PATIENT_LINKS = [
  { to: ROUTES.TESTS, label: 'Find a test' },
  { to: ROUTES.PACKAGES, label: 'Health packages' },
  { to: ROUTES.BUILD_PACKAGE, label: 'Build your own package' },
  { to: ROUTES.DOCTOR_CONSULT, label: 'Talk to a lab doctor' },
  { to: ROUTES.BOOKING, label: 'Book a home test' },
  { to: ROUTES.REPORTS, label: 'My reports' },
  { to: ROUTES.TRACK, label: 'Track sample' },
  { to: ROUTES.BLOG, label: 'Health guides' },
  { to: ROUTES.CITY, label: 'Tests in your city' },
];

const PARTNER_LINKS = [
  { to: ROUTES.PARTNER_DOCTORS, label: 'Doctors & clinics' },
  { to: ROUTES.PARTNER_LABS, label: 'Labs & collection centres' },
  { to: ROUTES.CORPORATE, label: 'Corporates' },
  { to: ROUTES.PARTNER_PHLEBOS, label: 'Phlebotomists' },
  { to: ROUTES.PARTNER_MANAGED_LABS, label: 'Managed labs' },
  { to: ROUTES.FRANCHISE, label: 'TrustLab franchisees' },
];

// All three point at the home page's apps section, as the reference does.
const APP_LINKS = [
  { to: `${ROUTES.HOME}#apps`, label: 'TrustLab (patients)' },
  { to: `${ROUTES.HOME}#apps`, label: 'TrustLab Partner (doctors)' },
  { to: `${ROUTES.HOME}#apps`, label: 'TrustLab Trustian (staff)' },
];

const COMPANY_LINKS = [
  { to: ROUTES.ABOUT, label: 'About TrustLab' },
  { to: ROUTES.CENTRES, label: 'Branches' },
  { to: ROUTES.CAREERS, label: 'Careers' },
  { to: ROUTES.HELP, label: 'Help & contact' },
  { to: ROUTES.LOGIN, label: 'Sign in' },
];

// One Legal page with three tabs, as the reference has it. These pointed
// at `/privacy#terms` and `/privacy#refunds` before the page existed, and
// both anchors were dead — the old privacy page has no such sections.
const LEGAL_LINKS = [
  { to: `${ROUTES.LEGAL}#privacy`, label: 'Privacy' },
  { to: `${ROUTES.LEGAL}#terms`, label: 'Terms' },
  { to: `${ROUTES.LEGAL}#refunds`, label: 'Refunds' },
];

const Footer = () => {
  const { data: contact } = useGetContactInfoQuery();

  const address = contact?.address || FALLBACK.address;
  const email = contact?.email || FALLBACK.email;

  // The reference's inline `color:#B6CFC8` outranks its own stylesheet, so
  // its footer links have no hover state at all. Lifting to white on hover
  // instead, matching how the header treats the same links — a link that
  // gives no feedback reads as plain text.
  const linkSx = {
    color: '#B6CFC8',
    textDecoration: 'none',
    width: 'fit-content',
    maxWidth: '100%',
    transition: 'color .2s',
    '&:hover': { color: '#FFFFFF' },
  };

  const columnTitleSx = {
    color: '#FFFFFF',
    fontWeight: 800,
  };

  const renderColumn = (title, links) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <Box component="span" sx={columnTitleSx}>{title}</Box>
      {links.map((l) => (
        <Box key={`${l.to}-${l.label}`} component={Link} to={l.to} sx={linkSx}>
          {l.label}
        </Box>
      ))}
    </Box>
  );

  return (
    <Box
      component="footer"
      sx={{
        mt: 'auto',
        bgcolor: '#0B2B27',
        color: '#B6CFC8',
        borderRadius: '36px 36px 0 0',
        fontFamily: "'Manrope', sans-serif",
      }}
    >
      <Box
        sx={{
          maxWidth: 1320,
          mx: 'auto',
          pt: 7,
          px: 3,
          pb: 3.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 4.5,
        }}
      >
        {/* auto-fit rather than a fixed column count: the five columns
            collapse to four, three, two and one on their own as the
            viewport narrows, with no breakpoints to maintain. min() keeps
            the 180px track from overflowing a 390px screen. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))',
            rowGap: 5,
            columnGap: 8,
            fontSize: 15,
            lineHeight: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              pr: 2,
              minWidth: 0,
            }}
          >
            <Box component={Link} to={ROUTES.HOME} sx={{ alignSelf: 'start', display: 'flex', lineHeight: 0 }}>
              <Logo sx={{ height: 'auto', maxHeight: 80, maxWidth: '100%' }} />
            </Box>
            <Box component="span" sx={{ lineHeight: 1.6 }}>{address}</Box>
            <Box
              component="a"
              href={`mailto:${email}`}
              sx={{ ...linkSx, color: '#FFFFFF', fontWeight: 700 }}
            >
              {email}
            </Box>
          </Box>

          {renderColumn('Patients', PATIENT_LINKS)}
          {renderColumn('Partners', PARTNER_LINKS)}
          {renderColumn('Our apps', APP_LINKS)}
          {renderColumn('Company', COMPANY_LINKS)}
        </Box>

        <Box
          sx={{
            borderTop: '1px solid #23463F',
            pt: 2.5,
            fontSize: 13,
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
          }}
        >
          <Box component="span">
            {`© ${new Date().getFullYear()} TrustLab Diagnostics Pvt Ltd · NABL ISO 15189:2022`}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {LEGAL_LINKS.map((l) => (
              <Box key={l.label} component={Link} to={l.to} sx={linkSx}>
                {l.label}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Footer;
